import type {ReactElement} from 'react';
import React, {useState, useEffect} from 'react';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {areSocialThumbnailsLoaded, getLoader, getPublishingOptions} from '@Components/email-marketing-campaigns/email-campaign-publish-options-library';
import {initEmailPublishingDialog, setPublishingOptionsFlow} from '@Components/email-marketing-campaigns/email-campaign-publish-options.reducer';
import {useEmailPublishingDialogSubflow} from '@Components/email-marketing-campaigns/email-campaign-publish-options.hooks';
import type {EmailCampaign} from '@Components/email-marketing-campaigns/email-marketing-campaigns.types';
import {EmailCampaignPublishingOptions, EmailCampaignPublishOptionsSublow} from '@Components/email-marketing-campaigns/email-marketing-campaigns.types';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {EmailCampaignPublishOptionsMenuPanel} from '@Panels/email-campaign-publish-options-panel/components/email-campaign-publish-options-menu-panel';
import {Text, TextSize} from '@Components/text';
import {EmailCampaignSendingOptionsPanelTypes} from '@Panels/email-campaign-sending-options-panel/email-campaign-sending-options-panel.types';
import {openEmailCampaignSendingOptionsModal} from '@Modals/email-campaign-sending-options-modal';
import {SkeletonBrick} from '@Components/skeleton-brick';
import styles from './email-maker-publishing-options-popup.module.scss';
import {useAppDispatch} from '@/hooks';

export interface EmailMakerPublishingOptionsPopupProps {
  selector: ReactElement<any>;
  getEmailCampaign: () => Promise<EmailCampaign | undefined>;
  dropdownAlignment?: ALIGNMENT_TYPE;
}

export default function EmailMakerPublishingOptionsPopup({selector, dropdownAlignment = ALIGNMENT_TYPE.BOTTOM_END, ...props}: EmailMakerPublishingOptionsPopupProps): ReactElement<any> {
  const dispatch = useAppDispatch();
  const [campaign, setCampaign] = useState<EmailCampaign | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const panelSubflow = useEmailPublishingDialogSubflow();

  useEffect(() => {
    if (!campaign) {
      return;
    }

    if (panelSubflow === EmailCampaignPublishOptionsSublow.SOCIAL_MEDIA) {
      openEmailCampaignSendingOptionsModal({
        campaignId: campaign.id,
        type: EmailCampaignSendingOptionsPanelTypes.SHARE_ON_SOCIAL_MEDIA,
      });

      return;
    }

    if (panelSubflow === EmailCampaignPublishOptionsSublow.WEB_PAGE) {
      openEmailCampaignSendingOptionsModal({
        campaignId: campaign.id,
        type: EmailCampaignSendingOptionsPanelTypes.SHARE_AS_WEBPAGE,
      });
    }
  }, [panelSubflow, campaign]);

  const getDropdownPopupHeader = (headerText: string, visibilityClasses: string): ReactElement<any> | null => {
    return (
      <div className={`${styles.dropdownHeader} ${visibilityClasses}`}>
        <Icon
          onClick={() => {
            dispatch(setPublishingOptionsFlow());
          }}
          className={styles.iconClasses}
          size={IconSize.SIZE_ICON_16}
          shape={IconShape.SQUARE}
          icon="icon-arrow-left"
          type={IconType.GHOST}
        />
        <Text bold size={TextSize.XSMALL} val={headerText} />
      </div>
    );
  };

  const getPublishOptionsPanel = (subflowType: EmailCampaignPublishOptionsSublow): ReactElement<any> | null => {
    if (!campaign) {
      return null;
    }

    const allPublishingOptions = getPublishingOptions(subflowType, campaign);
    const publishingOptionsForPopup = allPublishingOptions.filter((option) => {
      return option.id !== EmailCampaignPublishingOptions.EMAIL.valueOf();
    });

    return <EmailCampaignPublishOptionsMenuPanel className={`spacing-p-0 ${styles.popupOptionContainer}`} publishingOptions={publishingOptionsForPopup} />;
  };

  const toggleHeaderVisibilityClasses = (): string => {
    if (panelSubflow === EmailCampaignPublishOptionsSublow.MAIN_OPTIONS) {
      return `_hidden`;
    }

    return '';
  };

  const getClassesBySubflow = (subflowType: EmailCampaignPublishOptionsSublow): string => {
    if (subflowType === panelSubflow) {
      return '';
    }

    return '_hidden';
  };

  const getPopupOptions = (): ReactElement<any> => {
    if (isLoading) {
      return (
        <div>
          <SkeletonBrick isPulsating className="spacing-m-b-4" height={64} />
          <SkeletonBrick isPulsating height={64} />
        </div>
      );
    }

    return (
      <div className={styles.popupWrapper}>
        <div className={toggleHeaderVisibilityClasses()}>
          {getDropdownPopupHeader(window.i18next.t('pmwjs_publish_to_the_world'), getClassesBySubflow(EmailCampaignPublishOptionsSublow.SOCIAL_MEDIA))}
          {getDropdownPopupHeader(window.i18next.t('pmwjs_share_as_webpage'), getClassesBySubflow(EmailCampaignPublishOptionsSublow.WEB_PAGE))}
        </div>
        <div className={getClassesBySubflow(EmailCampaignPublishOptionsSublow.MAIN_OPTIONS)}>{getPublishOptionsPanel(EmailCampaignPublishOptionsSublow.MAIN_OPTIONS)}</div>
        <div className={!areSocialThumbnailsLoaded() && panelSubflow === EmailCampaignPublishOptionsSublow.SOCIAL_MEDIA ? '' : '_hidden'}>{getLoader(styles.loaderContainer)}</div>
      </div>
    );
  };

  const getDropdownPopup = (): ReactElement<any> => {
    return <div className={styles.dropdownPopupWrapper}>{getPopupOptions()}</div>;
  };

  const handleDropdownCTAClick = async (): Promise<void> => {
    setIsLoading(true);
    setIsOpen(true);

    const emailCampaign = await props.getEmailCampaign();

    if (!emailCampaign) {
      setIsOpen(false);
      return;
    }

    dispatch(initEmailPublishingDialog(emailCampaign.id));
    setCampaign(emailCampaign);
    setIsLoading(false);
  };

  const getDropdown = (): ReactElement<any> => {
    return (
      <Dropdown
        doCloseOnInnerClick
        alignment={dropdownAlignment}
        popupClassName={styles.popupClasses}
        popup={getDropdownPopup()}
        selector={selector}
        open={isOpen}
        onClick={() => {
          void handleDropdownCTAClick();
        }}
        onClose={() => {
          setIsOpen(false);
        }}
      />
    );
  };

  return <div>{getDropdown()}</div>;
}
